// @ts-nocheck
import React, { useCallback } from 'react';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import LockIcon from '@mui/icons-material/Lock';

import {
  blockInvalidNumbers,
  getErrorHelperText,
  isValueEmpty,
} from '../../../../utils/Utils';
import { ReactComponent as CalculationIcon } from '../../../../assets/images/CalculationIcon.svg';
import { INPUT_LABEL_PROPS } from './TextFieldComponent';

/**
 * Decimal field component for editing decimal numbers
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string|number} props.value - Current field value
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - The attribute being edited
 * @param {Object} props.entityDetailsFormikForm - Formik form instance for error handling
 * @returns {React.ReactElement} The TextField component with decimal type
 */
const DecimalFieldComponent = ({
  label,
  titleLabel,
  value,
  onChange,
  required = false,
  attribute,
  entityDetailsFormikForm,
  handleOpenDerivedModal,
  hideHeader,
  isLoadingConstraints = false,
}) => {
  const isDisabled = !attribute?.is_editable;
  const isDerived = hideHeader
    ? attribute?.is_derived && attribute?.permission === 'manage'
    : attribute?.is_derived;

  /**
   * Handles input change events
   * @function
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
   * @returns {void}
   */
  const handleChange = useCallback(
    (e) => {
      const inputValue = e?.target?.value;

      // mark this field touch immediately, so validation errors show on change
      if (!entityDetailsFormikForm?.touched?.[attribute?.id]) {
        // eslint-disable-next-line react/prop-types
        entityDetailsFormikForm?.setFieldTouched(attribute?.id, true, false);
      }

      // If the input is empty, pass null instead of converting to 0
      if (isValueEmpty(inputValue)) {
        onChange(null);
      } else {
        onChange(Number(inputValue));
      }
    },
    [onChange, entityDetailsFormikForm, attribute?.id]
  );

  /**
   * Handles keyboard events to block invalid characters for decimal inputs
   * @function
   * @param {React.KeyboardEvent} e - The keyboard event
   * @returns {void}
   */
  const handleKeyDown = useCallback(
    (e) =>
      ['Decimal'].includes(attribute?.data_type)
        ? blockInvalidNumbers(e, attribute?.data_type)
        : null,
    [blockInvalidNumbers, attribute]
  );

  /**
   * Prevents wheel events from changing the numeric input value
   * @function
   * @param {React.WheelEvent} e - The wheel event
   * @returns {void}
   */
  const handleWheel = useCallback((e) => {
    // Prevent the input value change on scroll
    e.target.blur();
  }, []);
  const handleClickAttribute = useCallback(
    (e) => {
      e.stopPropagation();
      handleOpenDerivedModal(attribute);
    },
    [handleOpenDerivedModal, attribute]
  );
  const getDecimalInputProps = useCallback(() => {
    if (attribute?.data_type !== 'Decimal') return {};

    return {
      inputProps: {
        min: 0,
        step: 'any',
      },
    };
  }, [attribute?.data_type]);

  const renderEndAdornment = useCallback(() => {
    return (
      <InputAdornment position="end">
        {isDerived ? (
          <Tooltip
            title={
              !attribute?.show_calculation_popUp
                ? 'Enable the calculation popup to calculate this attribute.'
                : `Calculate ${attribute?.label}`
            }
          >
            <IconButton
              sx={{ p: 0 }}
              onClick={handleClickAttribute}
              disabled={!attribute?.show_calculation_popUp}
            >
              <CalculationIcon />
            </IconButton>
          </Tooltip>
        ) : (
          isDisabled && (
            <Tooltip title="Field is locked or not editable">
              <LockIcon />
            </Tooltip>
          )
        )}
      </InputAdornment>
    );
  }, [isDerived, attribute?.label, handleClickAttribute, isDisabled]);

  return (
    <TextField
      {...entityDetailsFormikForm?.getFieldProps(attribute?.id)}
      type="number"
      label={label}
      title={titleLabel}
      placeholder={isDerived ? '-' : 'Enter a value'}
      value={value !== null && value !== undefined ? value : ''}
      disabled={isDisabled || isDerived || isLoadingConstraints}
      onChange={handleChange}
      required={required}
      onKeyDown={handleKeyDown}
      onWheel={handleWheel}
      fullWidth
      variant="outlined"
      size="medium"
      error={Boolean(
        getErrorHelperText(
          entityDetailsFormikForm?.errors[attribute?.id],
          entityDetailsFormikForm?.touched[attribute?.id]
        )
      )}
      helperText={getErrorHelperText(
        entityDetailsFormikForm?.errors[attribute?.id],
        entityDetailsFormikForm?.touched[attribute?.id]
      )}
      InputProps={{
        ...getDecimalInputProps(),
        endAdornment: renderEndAdornment(),
      }}
      InputLabelProps={INPUT_LABEL_PROPS}
    />
  );
};

DecimalFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    min_value: PropTypes.number,
    max_value: PropTypes.number,
    name: PropTypes.string,
    data_type: PropTypes.string,
    is_editable: PropTypes.bool,
    label: PropTypes.string,
    is_derived: PropTypes.string,
    permission: PropTypes.string,
    show_calculation_popUp: PropTypes.bool,
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.string),
    touched: PropTypes.objectOf(PropTypes.bool),
  }),
  handleOpenDerivedModal: PropTypes.func.isRequired,
  hideHeader: PropTypes.bool.isRequired,
  isLoadingConstraints: PropTypes.bool,
};

export default DecimalFieldComponent;
